import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useDonorContext } from '@/context'
import { useGetDonorBadges } from '@/hooks'
import { RecognitionBanner } from './RecognitionBanner'
import { MilestoneCollection } from './MilestoneCollection'
import { OtherEarnedBadges } from './OtherEarnedBadges'
import { BadgesSkeleton } from './Skeleton'

const SectionHeading = ({ title, count }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 2,
      mb: 2,
    }}
  >
    <Typography variant='h5' fontWeight={700}>
      {title}
    </Typography>
    <Typography variant='body1' fontWeight={700} sx={{ color: 'primary.main' }}>
      {count} Earned
    </Typography>
  </Box>
)

/**
 * My Badges (DEP-9449).
 *
 * Two sections: multi-level designations as expandable collections, then the
 * single-level ones as a grid. What the donor sees is decided entirely by the
 * endpoint - Display to Donor, earned levels and the credit settings are all
 * applied there (AC3, AC8), so this page renders what it is given rather than
 * filtering a staff-shaped payload.
 */
export const DonorBadges = () => {
  const { donor } = useDonorContext()
  const { getDonorBadges, loadingGetDonorBadges } = useGetDonorBadges()

  const [data, setData] = useState(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!donor?.id) return undefined
    ;(async () => {
      try {
        const response = await getDonorBadges(donor.id)
        if (!cancelled) setData(response?.data || null)
      } catch (error) {
        // The page still has a banner to show; an empty state is a better
        // answer here than a blank screen.
        if (!cancelled) setData(null)
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [donor?.id])

  const milestones = data?.milestone_badges || []
  const others = data?.other_badges || []

  if (loadingGetDonorBadges && !loaded) {
    return (
      <>
        <Typography variant='h3' fontWeight={700}>
          My Badges
        </Typography>
        <BadgesSkeleton />
      </>
    )
  }

  return (
    <>
      <Typography variant='h3' fontWeight={700}>
        My Badges
      </Typography>

      <Box sx={{ py: 4 }}>
        <RecognitionBanner />

        {/* AC9: the page stands on its own before the first badge is earned. */}
        {!milestones.length && !others.length ? (
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            You have not earned any badges yet. Every donation counts toward
            your first one.
          </Typography>
        ) : (
          <>
            {/* AC9: collections with nothing earned are not rendered at all,
                so an empty Milestone Badges section never appears. */}
            {milestones.length > 0 && (
              <Box sx={{ mb: 5 }}>
                <SectionHeading
                  title='Milestone Badges'
                  count={milestones.length}
                />
                {milestones.map(collection => (
                  <MilestoneCollection
                    key={collection.designation_id}
                    collection={collection}
                  />
                ))}
              </Box>
            )}

            {others.length > 0 && (
              <Box>
                <SectionHeading
                  title='Other Earned Badges'
                  count={others.length}
                />
                <OtherEarnedBadges badges={others} />
              </Box>
            )}
          </>
        )}
      </Box>
    </>
  )
}
